/*
 * The desktop dropdown's behaviour beyond Horizon's own (session MM1, 2026-10-10).
 * The markup is snippets/wc-mega-menu.liquid; Horizon's header-menu.js still
 * opens and closes each panel (hover, the disclosure button, Escape).
 *
 * This file does four things, all delegated from the document so that
 * Horizon's idle re-render of the header (hydrate) cannot orphan a listener:
 *
 * 1. PLACE the card under its own parent item. Its left edge lines up with
 *    the parent's text, less the card's padding, so the first link sits under
 *    the word the pointer is on, and it is clamped inside the header's page
 *    width so the right-hand items (Help) open leftward. It also sizes the
 *    bridge (the card's ::before) to the parent alone.
 * 2. LOAD the pictures. They are server-rendered with data-wc-mm-srcset, so
 *    none downloads with the page. A panel's pictures load when it opens;
 *    each panel's first picture loads at idle after the page has loaded, so
 *    the first look at a panel is not an empty frame.
 * 3. PREVIEW: hovering or focusing a link shows its picture.
 * 4. KEYS: ArrowDown on a parent opens its panel and moves into it; Up, Down,
 *    Home and End move between the panel's links. Escape is Horizon's.
 *
 * The promoted srcset survives hydrate because every <img> carries
 * data-skip-node-update, which morph honours for attributes. Placement and
 * the active picture are recomputed on every open, so a re-render while a
 * panel is open costs nothing worse than the centred fallback.
 */

const CARD = "[data-wc-mega-menu]";
const ITEM = ".menu-list__list-item";
const LINKS = ".wc-mega-menu__link, .wc-mega-menu__sublink, .wc-mega-menu__all, .wc-mega-menu__promo-link";

/** @param {HTMLImageElement} img */
function promote(img) {
  const srcset = img.dataset.wcMmSrcset;
  if (!srcset || img.getAttribute("srcset")) return;
  img.setAttribute("srcset", srcset);
  if (img.dataset.wcMmSrc) img.setAttribute("src", img.dataset.wcMmSrc);
}

/** @param {Element} card */
function loadPanel(card) {
  card.querySelectorAll("img[data-wc-mm-srcset]").forEach((img) => promote(/** @type {HTMLImageElement} */ (img)));
}

function loadFirstOfEach() {
  document.querySelectorAll(CARD).forEach((card) => {
    const first = card.querySelector(".wc-mega-menu__preview[data-active] img, .wc-mega-menu__promo-image");
    if (first) promote(/** @type {HTMLImageElement} */ (first));
  });
}

/**
 * @param {Element} card
 * @param {Element} item
 */
function place(card, item) {
  if (!(card instanceof HTMLElement)) return;
  const inner = card.parentElement;
  const trigger = item.querySelector(".menu-list__link-title") || item;
  if (!inner) return;

  const header = card.closest("#header-component");
  const columns = header?.querySelector(".header__row--top .header__columns") || header || inner;

  const innerRect = inner.getBoundingClientRect();
  const innerStyle = getComputedStyle(inner);
  const contentLeft = innerRect.left + parseFloat(innerStyle.paddingLeft || "0");
  const bounds = columns.getBoundingClientRect();
  const triggerRect = trigger.getBoundingClientRect();
  // The title's own padding: line the links up with its TEXT, not its box.
  const textLeft = triggerRect.left + parseFloat(getComputedStyle(trigger).paddingLeft || "0");
  const width = card.offsetWidth;
  const pad = parseFloat(getComputedStyle(card).paddingLeft || "0");

  // The card's padding box starts at the parent's text, less the padding.
  let left = textLeft - pad - 1;
  const max = bounds.right - width;
  if (left > max) left = max;
  if (left < bounds.left) left = bounds.left;

  card.style.setProperty("--wc-mega-menu-x", `${Math.round(left - contentLeft)}px`);
  card.style.setProperty("--wc-mega-menu-bridge-x", `${Math.round(triggerRect.left - left)}px`);
  card.style.setProperty("--wc-mega-menu-bridge-w", `${Math.round(triggerRect.width)}px`);
  card.setAttribute("data-placed", "");
}

/**
 * @param {Element} card
 * @param {number} index
 */
function show(card, index) {
  const previews = card.querySelectorAll(".wc-mega-menu__preview");
  if (!previews.length) return;
  const target = card.querySelector(`.wc-mega-menu__preview[data-wc-mm-preview="${index}"]`);
  // A link with no picture of its own still has the panel default; a link the
  // server rendered no picture for leaves the current one showing.
  if (!target) return;
  previews.forEach((el) => el.toggleAttribute("data-active", el === target));
  card.querySelectorAll(".wc-mega-menu__link").forEach((a) => {
    a.toggleAttribute("data-current", a.getAttribute("data-wc-mm-index") === String(index));
  });
}

/** @param {Element} card */
function reset(card) {
  show(card, 0);
  card.querySelectorAll(".wc-mega-menu__link[data-current]").forEach((a) => a.removeAttribute("data-current"));
}

/** @param {EventTarget | null} target */
function opened(target) {
  if (!(target instanceof Element)) return;
  const item = target.closest(ITEM);
  const card = item?.querySelector(CARD);
  if (!item || !card) return;
  loadPanel(card);
  place(card, item);
}

/** @param {Event} event */
function onOver(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = target.closest("[data-wc-mm-index]");
  const card = target.closest(CARD);
  if (link && card) {
    show(card, Number(link.getAttribute("data-wc-mm-index")));
    return;
  }
  if (!card) {
    const item = target.closest(ITEM);
    const own = item?.querySelector(CARD);
    if (own && !item.contains(event.relatedTarget instanceof Node ? event.relatedTarget : null)) {
      reset(own);
    }
    opened(target);
  }
}

/** @param {KeyboardEvent} event */
function onKeydown(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  const key = event.key;
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(key)) return;

  const card = target.closest(CARD);
  if (card) {
    const links = /** @type {HTMLElement[]} */ ([...card.querySelectorAll(LINKS)]);
    const at = links.indexOf(target);
    if (at < 0) return;
    event.preventDefault();
    let next = at;
    if (key === "ArrowDown") next = Math.min(links.length - 1, at + 1);
    if (key === "ArrowUp") next = at - 1;
    if (key === "Home") next = 0;
    if (key === "End") next = links.length - 1;
    if (next < 0) {
      // Up from the first link goes back to the parent's disclosure button.
      const back = target.closest(ITEM)?.querySelector(".menu-list__disclosure, .menu-list__link");
      if (back instanceof HTMLElement) back.focus();
      return;
    }
    links[next].focus();
    return;
  }

  // ArrowDown on a parent (its link or its disclosure button) opens the panel
  // and moves into it.
  if (key !== "ArrowDown") return;
  const item = target.closest(ITEM);
  const own = item?.querySelector(CARD);
  if (!item || !own || !target.matches(".menu-list__link, .menu-list__disclosure")) return;
  event.preventDefault();
  const disclosure = item.querySelector(".menu-list__disclosure");
  if (disclosure instanceof HTMLElement && disclosure.getAttribute("aria-expanded") !== "true") disclosure.click();
  opened(item);
  requestAnimationFrame(() => {
    const first = own.querySelector(LINKS);
    if (first instanceof HTMLElement) first.focus();
  });
}

if (!window.__wcMegaMenu) {
  window.__wcMegaMenu = true;
  document.addEventListener("pointerover", onOver, { passive: true });
  document.addEventListener("focusin", onOver);
  document.addEventListener("keydown", onKeydown);
  // A click on the disclosure button opens without a pointerover on touch-free
  // keyboards and screen readers: place and load then too.
  document.addEventListener("click", (event) => opened(event.target), { capture: true, passive: true });

  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
  const atIdle = () => idle(loadFirstOfEach, { timeout: 4000 });
  if (document.readyState === "complete") atIdle();
  else window.addEventListener("load", atIdle, { once: true });

  // Re-place an open card when the window changes size.
  window.addEventListener(
    "resize",
    () => {
      const open = document.querySelector(`${ITEM}:has(.menu-list__submenu:not([inert])) ${CARD}`);
      if (open) opened(open);
    },
    { passive: true },
  );
}
